<template>
  <b-card>
    <div>
      <!-- search input -->
      <b-row class="mb-1 align-items-center">
        <!-- Search input -->
        <b-col cols="12" sm="6" lg="4" class="mb-1 mb-sm-0">
          <b-form-input
              v-if="$permissionAbility(BANK_SLIP_TEMPLATE_SHOW, permissions)"
              v-model.lazy="searchTerm"
              placeholder="Search..."
              type="text"
              class="d-inline-block w-100"
              @keyup="onSearch"
          />
        </b-col>

        <!-- Create button -->
        <b-col
            cols="12"
            sm="6"
            lg="8"
            class="d-flex justify-content-center justify-content-sm-end"
        >
          <template v-if="$permissionAbility(BANK_SLIP_TEMPLATE_CREATE, permissions)">
            <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="flex-shrink-0"
                @click="showModal"
            >
              Create
            </b-button>
          </template>
        </b-col>
      </b-row>

      <!-- table -->
      <vue-good-table
          v-if="$permissionAbility(BANK_SLIP_TEMPLATE_SHOW, permissions)"
          styleClass="vgt-table table-custom-style striped"
          :line-numbers="false"
          mode="remote"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-column-filter="onColumnFilter"
          @on-per-page-change="onPerPageChange"
          :totalRows="totalRecords"
          :isLoading.sync="isLoading"
          :rows="rows"
          :columns="columns"
          :sort-options="{
            enabled: false,
            multipleColumns: true,
            initialSortBy: [{ field: 'created_at', type: 'desc' }],
          }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
          }"

      >
        <template slot="table-row" slot-scope="props">

            <span v-if="props.column.field === 'format_default'">
              <b-badge v-if="props.row?.default" variant="success">Yes</b-badge>
              <b-badge v-else variant="danger">No</b-badge>
            </span>
            <span v-if="props.column.field === 'action'">

              <span>
                <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
              >
                  <template v-slot:button-content>
                    <feather-icon
                        icon="MoreVerticalIcon"
                        size="16"
                        class="text-body align-middle mr-25"
                    />
                  </template>

                  <template v-if="$permissionAbility(BANK_SLIP_TEMPLATE_EDIT, permissions)">

                    <b-dropdown-item v-on:click="onShow(props.row)">
                      <feather-icon icon="Edit2Icon" class="mr-50" />
                      <span>Edit</span>
                    </b-dropdown-item>
                    </template>

                  <template v-if="$permissionAbility(BANK_SLIP_TEMPLATE_DELETE, permissions)">
                    <b-dropdown-item v-on:click="onDelete(props.row)">
                      <feather-icon icon="TrashIcon" class="mr-50" />
                      <span>Delete</span>
                    </b-dropdown-item>
                    </template>

              </b-dropdown>
              </span>
            </span>

          <!-- Column: Common -->
          <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                  v-model="pageLength"
                  :options="['10', '25', '50', '100', '500']"
                  class="mx-1"
                  @input="
                    (value) => props.perPageChanged({ currentPerPage: value })
                  "
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>

    <b-modal
        id="modal-create-form"
        centered
        size="lg"
        :title="
          modelType == 'editModel' ? 'Edit Template' : 'Create Template'
        "
        hide-footer
        @hidden="hiddenModal"
        no-close-on-backdrop
    >
      <validation-observer ref="createFormValidation">
        <b-form v-on:submit.prevent="validationForm">
          <!-- name -->
          <b-form-group
              label="Name"
              label-for="name"
              class="required-label"
          >
            <validation-provider
                #default="{ errors }"
                name="Template name"
                vid="name"
                rules="required|max:255"
            >
              <b-form-input
                  id="name"
                  type="text"
                  v-model="templateName"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Template name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
              label="Default"
              label-for="default"
              class=""
          >
            <ValidationProvider
                name="default"
                v-slot="{ errors }"
                vid="default"
                rules="required"
            >
              <v-select
                  v-model="defaultId"
                  :options="default_option"
                  :reduce="(item) => item.value"
                  label="name"
                  placeholder="Select Default"
                  class="mr-sm-1 mb-1 mb-sm-0 custom-font"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>

          <b-form-group
              label="Template"
              label-for="template"
              class="required-label"
          >
            <validation-provider
                name="template"
                vid="template"
                v-slot="{ errors }"
            >
              <div :class="{ fullscreen: isFullscreen }">
                <quill-editor
                    v-model="template"
                    :options="editorOption"
                    :style="editorStyle"
                    class="custom-quill-editor"
                >
                  <div id="toolbar" slot="toolbar">
                      <span class="ql-formats">
                        <button class="ql-bold">Bold</button>
                        <button class="ql-italic">Italic</button>
                        <button class="ql-underline">Underline</button>
                        <button class="ql-strike">Strike</button>
                        <button class="ql-blockquote"></button>
                        <button class="ql-list" value="ordered"></button>
                        <button class="ql-list" value="bullet"></button>
                        <button class="ql-script" value="sub"></button>
                        <button class="ql-script" value="super"></button>
                        <button class="ql-indent" value="-1"></button>
                        <button class="ql-indent" value="+1"></button>
                        <button class="ql-direction" value="rtl"></button>
                        <button class="ql-align" value=""></button>
                        <button class="ql-align" value="center"></button>
                        <button class="ql-align" value="right"></button>
                        <button class="ql-align" value="justify"></button>
                        <select class="ql-color"></select>
                        <select class="ql-background"></select>
                        <select class="ql-size">
                          <option value="small"></option>
                          <option selected></option>
                          <option value="large"></option>
                          <option value="huge"></option>
                        </select>
                        <select class="ql-font"></select>
                        <select class="ql-header">
                          <option value="1">Heading 1</option>
                          <option value="2">Heading 2</option>
                          <option value="3">Heading 3</option>
                          <option value="4">Heading 4</option>
                          <option value="5">Heading 5</option>
                          <option value="6">Heading 6</option>
                          <option selected>Normal</option>
                        </select>
                        <button class="ql-link"></button>

                        <!-- Fullscreen Button -->
                        <button type="button" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'" class="fullscreen-btn" @click="toggleFullscreen">
                          🔳
                        </button>
                      </span>
                  </div>
                </quill-editor>
              </div>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b>[[AMOUNT]] use this to placeholder to show amount (Ex: 10,770.00/- in words: (Ten Thousand Seven Hundred Seventy))</b>

          <!-- loading button -->
          <template v-if="isSubmitLoading">
            <b-button class="float-right" variant="primary" disabled>
              <b-spinner small />
              Loading...
            </b-button>
          </template>

          <!-- submit button -->
          <template v-else>
            <b-button
                type="submit"
                class="float-right"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>


  </b-card>
</template>

<script>
import "quill/dist/quill.core.css";
// eslint-disable-next-line
import "quill/dist/quill.snow.css";
import { quillEditor } from "vue-quill-editor";

import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BButton,
  BForm,
  BModal,
  BSpinner,
  BRow,
  BCol,
  VBTooltip,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, max } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import UserAssignPolicyForm from "@/views/admin/payroll-management/UserAssignPolicyForm.vue";
import {
  BANK_SLIP_TEMPLATE_ACCESS,
  BANK_SLIP_TEMPLATE_CREATE,
  BANK_SLIP_TEMPLATE_EDIT,
  BANK_SLIP_TEMPLATE_DELETE,
  BANK_SLIP_TEMPLATE_SHOW,
} from "@/helpers/permissionsConstant";

export default {
  name: "BankSlipTemplate",
  components: {
    UserAssignPolicyForm,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    quillEditor,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BRow,
    BCol,
    VBTooltip,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      // Permissions
      BANK_SLIP_TEMPLATE_ACCESS,
      BANK_SLIP_TEMPLATE_CREATE,
      BANK_SLIP_TEMPLATE_EDIT,
      BANK_SLIP_TEMPLATE_DELETE,
      BANK_SLIP_TEMPLATE_SHOW,

      default_option: [
        { name: 'Yes', value: 1 },
        { name: 'No', value: 0 },
      ],
      defaultId: 0,
      isFullscreen: false,
      template: "",
      editorStyle: {
        minHeight: "13rem", // Min height for the editor
        overflowY: "auto",  // Enable scroll if content overflows
      },
      editorOption: {
        modules: {
          toolbar: "#toolbar",
        },
      },

      modelType: "",
      name: "",

      templateId: "",
      templateName: "",

      //table
      pageLength: 10,
      columns: [
        {
          label: "Name",
          field: "name",
          sortable: false,
        },
        {
          label: "Default",
          field: "format_default",
          sortable: false,
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: "",
      delayTimer: null,
      isLoading: false,
      isSubmitLoading: false,
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: [
          { field: "id", type: "desc" },
          { field: "created_at", type: "desc" },
        ],
        page: 1,
        perPage: 10,
      },
    };
  },

  computed: {
    ...mapGetters({
      permissions: "userModule/getPermissions",
    }),
    statusVariant() {
      const statusColor = {
        true: "light-success",
        false: "light-danger",
      };

      return (status) => statusColor[status];
    },
  },

  async created() {
    try {
      this.loadItems();
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message,
        },
      });
    }
  },

  methods: {

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },

    showModal() {
      this.$bvModal.show("modal-create-form");
    },

    hiddenModal() {
      this.$bvModal.hide("modal-create-form");
      this.resetModal();
    },

    resetModal() {
      this.modelType = "";
      this.templateId = "";
      this.template = "";
      this.defaultId = 0;

      this.templateName = "";

    },

    onShow(value) {
      this.modelType = "editModel";
      this.templateId = value.id
      this.template = value.content
      this.defaultId = value.default

      this.templateName = value.name;

      this.showModal();
    },

    onShowAssignUserModal(value) {
      this.templateId = value?.id;
      this.templateName = value?.name;

    },

    formatDateTime(value) {
      if (value) {
        return this.$moment(value).format("MMM Do YYYY");
      }
    },

    async onDelete(row) {
      this.$swal({
        title: "Warning!",
        text: "Are You Sure You Want To Delete "+ row.name +"?",
        icon: "warning",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Ok",
        showLoaderOnConfirm: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.$api.delete(`api/bank-slip-template/${row.id}`);

            this.loadItems();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Template Successfully Deleted",
              },
            });
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message,
              },
            });
          }
        }
      });
    },

    onSearch() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer);
        this.delayTimer = null;
      }

      this.delayTimer = setTimeout(() => {
        this.loadItems();
      }, 1000);
    },

    async getTemplateItems(params) {
      return await this.$api.get("api/bank-slip-template", {
        params: {
          show: params.show,
          page: params.page,
          sort: params.sort,
          q: params.q,
        },
      });
    },

    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onColumnFilter(params) {
      this.updateParams(params);
      this.loadItems();
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.loadItems();
    },

    onSortChange(params) {
      this.updateParams({
        sort: params,
      });
      this.loadItems();
    },

    async loadItems() {
      try {
        this.isLoading = true;
        const template = await this.getTemplateItems({
          show: this.serverParams.perPage,
          page: this.serverParams.page,
          sort: this.serverParams.sort,
          q: this.searchTerm,
        });

        const data = template?.data?.data;
        const meta = template?.data?.meta;

        this.totalRecords = meta?.pagination?.total;
        this.rows = data;

        this.isLoading = true;

      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            icon: "BellIcon",
            variant: "warning",
            text: error?.response?.data?.message,
          },
        });
      }
    },
    validationForm: async function () {
      this.$refs.createFormValidation.validate().then(async (success) => {
        if (success) {
          try {
            this.$refs.createFormValidation.reset();
            if (this.modelType == "editModel") {
              this.isSubmitLoading = true;
              await this.$api.put(
                  `/api/bank-slip-template/${this.templateId}`,
                  {
                    name: this.templateName,
                    content: this.template,
                    default: this.defaultId,
                  }
              );
              this.isSubmitLoading = false;
              this.loadItems();

              this.hiddenModal();

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Success",
                  icon: "BellIcon",
                  variant: "success",
                  text: "Template Successfully Updated",
                },
              });
            } else {
              this.isSubmitLoading = true;
              await this.$api.post("api/bank-slip-template", {
                name: this.templateName,
                content: this.template,
                default: this.defaultId,
              });
              this.isSubmitLoading = false;
              this.hiddenModal();

              this.loadItems();

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Success",
                  icon: "BellIcon",
                  variant: "success",
                  text: "Template Successfully Created",
                },
              });
            }
          } catch (error) {
            this.isSubmitLoading = false;
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }

            if (error?.response?.data?.errors) {
              this.$refs.createFormValidation.setErrors(
                  error?.response?.data?.errors
              );
            }
          }
        }
      });
    },


  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

.ql-editor .ql-font-timesnewroman {
  font-family: "Times New Roman", Times, serif;
}
@import "@core/scss/vue/libs/quill.scss";
.ql-editor {
  transition: height 0.2s ease;
  min-height: 13rem;
}

/* Fullscreen mode */
.fullscreen {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  z-index: 9999;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* Adjust editor and toolbar in fullscreen mode */
.fullscreen .ql-container {
  height: calc(100vh - 50px) !important;
}

.fullscreen .ql-toolbar {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1000;
  padding: 10px;
}

/* Fullscreen button styling */
.fullscreen-btn {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 18px;
  margin-left: 10px;
}

.required-label label::after {
  content: " *";
  color: red;
}
</style>


